import type { Split } from '@cadencia/contracts';
import type { SessionTemplate } from './block-builder.js';

/**
 * Plantillas de sesion por SPLIT (docs/TRAINING_ENGINE.md §3 y §7.1).
 *
 * Los huecos (`slots`) son un ROL mas una lista de candidatos EN ORDEN DE
 * PREFERENCIA; se elige el primero cuyo equipamiento exista en el gimnasio del
 * usuario. Esto es una simplificacion declarada del §7.1, que describe una
 * puntuacion con seis criterios: la lista ordenada ya codifica la parte estable
 * de esa puntuacion (patron, perfil de resistencia, coste sistemico), y los
 * criterios que dependen del historial —cobertura semanal, progreso reciente,
 * variedad— no tienen datos que leer cuando se construye el primer bloque.
 *
 * REVISAR cuando exista historial suficiente: la seleccion deberia pasar a
 * puntuar candidatos con `substitution.ts` en lugar de coger el primero.
 */

/**
 * Torso/Pierna a 4 dias (T-P-T-P): frecuencia 2 por musculo con 4 sesiones y
 * encaja en 90 min. Se elige antes que PPL porque PPL a 4 dias da frecuencia
 * irregular (§3).
 *
 * Los objetivos son NEUTROS respecto a la fase a proposito: la misma plantilla
 * sirve para readaptar y para ganar masa, y quien matiza el objetivo es la fase
 * (ver `readaptation-block.ts`, que los reescribe en clave de reintroduccion).
 */
export const TORSO_LEG_SESSIONS: SessionTemplate[] = [
  {
    name: 'Torso A',
    objective: 'Empuje horizontal y tracción vertical',
    orderInWeek: 0,
    slots: [
      { role: 'main', candidates: ['press-banca-mancuernas', 'press-banca-barra', 'press-pecho-maquina', 'press-banca-multipower', 'flexiones'] },
      { role: 'main', candidates: ['jalon-polea-alta', 'jalon-maquina', 'dominadas', 'remo-invertido'] },
      { role: 'secondary', candidates: ['press-hombro-mancuernas', 'press-hombro-maquina', 'press-militar-multipower'] },
      { role: 'secondary', candidates: ['remo-gironda', 'remo-maquina', 'remo-mancuerna-una-mano'] },
      { role: 'accessory', candidates: ['elevaciones-laterales', 'elevaciones-laterales-polea', 'elevaciones-laterales-maquina'] },
      { role: 'accessory', candidates: ['extension-polea-cuerda', 'extension-triceps-maquina', 'fondos-banco'] },
      // Deltoide posterior y NO biceps. Medido: con el biceps aqui, la
      // plantilla dejaba el deltoide posterior en 4 series/semana —la mitad de
      // su minimo (§4.2)— mientras el biceps rozaba su maximo alimentado por
      // los jalones y remos. Cambiar el hueco lo arregla sin alargar la sesion,
      // que ya iba a 80 min en las semanas altas.
      { role: 'accessory', candidates: ['face-pull', 'pajaros-maquina', 'pajaros-mancuernas'] },
    ],
  },
  {
    name: 'Pierna A',
    objective: 'Sentadilla y bisagra con carga axial contenida',
    orderInWeek: 1,
    slots: [
      { role: 'main', candidates: ['prensa-45', 'hack-squat', 'sentadilla-multipower', 'prensa-horizontal', 'sentadilla-goblet'] },
      { role: 'main', candidates: ['peso-muerto-rumano-mancuernas', 'peso-muerto-rumano', 'curl-femoral-tumbado'] },
      { role: 'secondary', candidates: ['extension-cuadriceps', 'sissy-squat'] },
      // Gluteo y NO isquios. Medido: con un segundo hueco de isquios aqui —el
      // principal ya es peso muerto rumano— la plantilla llegaba a 25
      // series/semana en las semanas altas, por encima del maximo de 22 (§4.2),
      // mientras el gluteo se quedaba corto. Cuatro huecos de isquios en un
      // torso/pierna son demasiados.
      { role: 'secondary', candidates: ['hip-thrust', 'zancada-inversa', 'step-up'] },
      { role: 'accessory', candidates: ['elevacion-gemelos-de-pie', 'elevacion-gemelos-prensa', 'elevacion-gemelos-mancuerna'] },
      { role: 'accessory', candidates: ['plancha', 'pallof-press', 'crunch-polea'] },
    ],
  },
  {
    name: 'Torso B',
    objective: 'Empuje vertical y tracción horizontal',
    orderInWeek: 2,
    slots: [
      { role: 'main', candidates: ['press-inclinado-mancuernas', 'press-banca-inclinado-barra', 'press-pecho-maquina'] },
      { role: 'main', candidates: ['remo-mancuerna-una-mano', 'remo-maquina', 'remo-gironda', 'remo-invertido'] },
      { role: 'secondary', candidates: ['press-hombro-maquina', 'press-hombro-mancuernas'] },
      { role: 'secondary', candidates: ['jalon-agarre-neutro', 'jalon-polea-alta', 'jalon-maquina'] },
      { role: 'accessory', candidates: ['pajaros-mancuernas', 'pajaros-maquina', 'face-pull'] },
      { role: 'accessory', candidates: ['aperturas-polea', 'peck-deck', 'aperturas-mancuernas'] },
      { role: 'accessory', candidates: ['curl-martillo', 'curl-barra-z', 'curl-polea-baja'] },
    ],
  },
  {
    name: 'Pierna B',
    objective: 'Trabajo unilateral y cadena posterior',
    orderInWeek: 3,
    slots: [
      { role: 'main', candidates: ['peso-muerto-rumano', 'peso-muerto-rumano-mancuernas', 'hip-thrust'] },
      { role: 'main', candidates: ['prensa-45', 'hack-squat', 'sentadilla-multipower', 'prensa-horizontal'] },
      { role: 'secondary', candidates: ['bulgara', 'zancada-inversa', 'step-up', 'zancada-mancuernas'] },
      { role: 'secondary', candidates: ['curl-femoral-sentado', 'curl-femoral-tumbado'] },
      { role: 'accessory', candidates: ['elevacion-gemelos-sentado', 'elevacion-gemelos-de-pie', 'elevacion-gemelos-mancuerna'] },
      { role: 'accessory', candidates: ['rueda-abdominal', 'elevacion-piernas-colgado', 'plancha'] },
    ],
  },
];

/**
 * Cuerpo completo, para quien entrena DOS o TRES días (docs/TEST_INICIAL.md
 * §2.4). Hasta el test inicial solo existía el torso/pierna de cuatro, así que
 * sin IA no se podía respetar otra disponibilidad.
 *
 * Mismos candidatos que el torso/pierna, en el mismo orden de preferencia: la
 * selección por gimnasio y por limitaciones es la misma y no hay dos criterios
 * que puedan separarse. Lo que cambia es el reparto —cada sesión toca todo el
 * cuerpo— para que con dos o tres días cada músculo se entrene más de una vez
 * a la semana.
 *
 * A y B son las dos de dos días; con tres se añade C, que cubre lo que A y B
 * dejan en una sola exposición (inclinado, isquios en flexión, gemelo, core).
 */
const CUERPO_COMPLETO_A: SessionTemplate = {
  name: 'Cuerpo completo A',
  objective: 'Sentadilla, empuje horizontal y tracción vertical',
  orderInWeek: 0,
  slots: [
    { role: 'main', candidates: ['prensa-45', 'hack-squat', 'sentadilla-multipower', 'prensa-horizontal', 'sentadilla-goblet'] },
    { role: 'main', candidates: ['press-banca-mancuernas', 'press-banca-barra', 'press-pecho-maquina', 'press-banca-multipower', 'flexiones'] },
    { role: 'secondary', candidates: ['jalon-polea-alta', 'jalon-maquina', 'dominadas', 'remo-invertido'] },
    { role: 'secondary', candidates: ['peso-muerto-rumano-mancuernas', 'peso-muerto-rumano', 'curl-femoral-tumbado'] },
    { role: 'accessory', candidates: ['elevaciones-laterales', 'elevaciones-laterales-polea', 'elevaciones-laterales-maquina'] },
    { role: 'accessory', candidates: ['face-pull', 'pajaros-maquina', 'pajaros-mancuernas'] },
  ],
};

const CUERPO_COMPLETO_B: SessionTemplate = {
  name: 'Cuerpo completo B',
  objective: 'Bisagra, empuje vertical y tracción horizontal',
  orderInWeek: 1,
  slots: [
    { role: 'main', candidates: ['peso-muerto-rumano', 'peso-muerto-rumano-mancuernas', 'hip-thrust'] },
    { role: 'main', candidates: ['press-hombro-mancuernas', 'press-hombro-maquina', 'press-militar-multipower'] },
    { role: 'secondary', candidates: ['remo-mancuerna-una-mano', 'remo-maquina', 'remo-gironda', 'remo-invertido'] },
    { role: 'secondary', candidates: ['bulgara', 'zancada-inversa', 'step-up', 'zancada-mancuernas'] },
    { role: 'accessory', candidates: ['aperturas-polea', 'peck-deck', 'aperturas-mancuernas'] },
    { role: 'accessory', candidates: ['curl-martillo', 'curl-barra-z', 'curl-polea-baja'] },
    { role: 'accessory', candidates: ['extension-polea-cuerda', 'extension-triceps-maquina', 'fondos-banco'] },
  ],
};

const CUERPO_COMPLETO_C: SessionTemplate = {
  name: 'Cuerpo completo C',
  objective: 'Empuje inclinado, cuádriceps y cadena posterior',
  orderInWeek: 2,
  slots: [
    { role: 'main', candidates: ['press-inclinado-mancuernas', 'press-banca-inclinado-barra', 'press-pecho-maquina'] },
    { role: 'main', candidates: ['hack-squat', 'prensa-45', 'sentadilla-multipower', 'prensa-horizontal', 'sentadilla-goblet'] },
    { role: 'secondary', candidates: ['jalon-agarre-neutro', 'jalon-polea-alta', 'jalon-maquina'] },
    { role: 'secondary', candidates: ['curl-femoral-sentado', 'curl-femoral-tumbado'] },
    { role: 'accessory', candidates: ['elevacion-gemelos-de-pie', 'elevacion-gemelos-prensa', 'elevacion-gemelos-mancuerna'] },
    { role: 'accessory', candidates: ['plancha', 'pallof-press', 'crunch-polea'] },
  ],
};

export const FULL_BODY_2_SESSIONS: SessionTemplate[] = [CUERPO_COMPLETO_A, CUERPO_COMPLETO_B];
export const FULL_BODY_3_SESSIONS: SessionTemplate[] = [
  CUERPO_COMPLETO_A,
  CUERPO_COMPLETO_B,
  CUERPO_COMPLETO_C,
];

/**
 * Cinco días: el torso/pierna de siempre y una quinta sesión de REFUERZO. No es
 * un split nuevo, y por eso se guarda como `torso_leg`: la estructura sigue
 * siendo la de cuatro, con un día más.
 *
 * El refuerzo es solo de accesorios, y a propósito. La primera versión repetía
 * el cuerpo completo C y, medido en ganancia muscular, sacaba de su banda a
 * cinco músculos a la vez —isquios 27 de 22, dorsal 24 de 22, hombro anterior
 * 20,5 de 16— (§4.2). Los grandes ya van cubiertos con dos exposiciones en las
 * cuatro sesiones; lo que admite una tercera sin pasarse son los pequeños.
 */
const REFUERZO: SessionTemplate = {
  name: 'Refuerzo',
  objective: 'Hombro lateral y posterior, brazos, gemelo y core',
  orderInWeek: 4,
  slots: [
    { role: 'accessory', candidates: ['elevaciones-laterales-polea', 'elevaciones-laterales', 'elevaciones-laterales-maquina'] },
    { role: 'accessory', candidates: ['pajaros-maquina', 'face-pull', 'pajaros-mancuernas'] },
    { role: 'accessory', candidates: ['curl-polea-baja', 'curl-barra-z', 'curl-martillo'] },
    { role: 'accessory', candidates: ['extension-triceps-maquina', 'extension-polea-cuerda', 'fondos-banco'] },
    { role: 'accessory', candidates: ['elevacion-gemelos-sentado', 'elevacion-gemelos-de-pie', 'elevacion-gemelos-mancuerna'] },
    { role: 'accessory', candidates: ['crunch-polea', 'pallof-press', 'plancha'] },
  ],
};

export const TORSO_LEG_5_SESSIONS: SessionTemplate[] = [...TORSO_LEG_SESSIONS, REFUERZO];

/** Qué split y qué sesiones corresponden a N días de entrenamiento. */
export function sessionsForDays(days: number): { split: Split; sessions: SessionTemplate[] } {
  if (days <= 2) return { split: 'full_body', sessions: FULL_BODY_2_SESSIONS };
  if (days === 3) return { split: 'full_body', sessions: FULL_BODY_3_SESSIONS };
  if (days === 4) return { split: 'torso_leg', sessions: TORSO_LEG_SESSIONS };
  return { split: 'torso_leg', sessions: TORSO_LEG_5_SESSIONS };
}

/**
 * Splits con plantillas escritas. `upper_lower` apunta a las mismas: en este
 * catalogo «torso» y «tren superior» cubren los mismos patrones, y duplicar las
 * cuatro listas de candidatos solo garantizaria que se separen con el tiempo.
 *
 * `full_body` las tiene desde el test inicial (23/09/2026, docs/TEST_INICIAL.md
 * §2.4). `push_pull_legs` y `custom` siguen SIN plantillas. Se declara aqui de
 * forma explicita en lugar de improvisarlas: una plantilla de split es una
 * decision de programacion, no un hueco que rellenar por simetria.
 */
const PLANTILLAS_POR_SPLIT: Partial<Record<Split, SessionTemplate[]>> = {
  torso_leg: TORSO_LEG_SESSIONS,
  upper_lower: TORSO_LEG_SESSIONS,
  // Desde el test inicial (23/09/2026). La de tres días: la de dos la elige
  // `sessionsForDays`, que es quien sabe cuántos días hay.
  full_body: FULL_BODY_3_SESSIONS,
};

/** Split con plantillas al que se recurre cuando el pedido no las tiene. */
export const DEFAULT_SPLIT: Split = 'torso_leg';

export function hasTemplates(split: Split): boolean {
  return PLANTILLAS_POR_SPLIT[split] !== undefined;
}

/** Plantillas del split, o `null` si ese split no tiene ninguna escrita. */
export function sessionsForSplit(split: Split): SessionTemplate[] | null {
  return PLANTILLAS_POR_SPLIT[split] ?? null;
}
